/// <reference types="vite-plugin-svgr/client" />

import "./App.scss";
import "./i18n/i18n";

import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";

import Navbar from "@/components/Navbar/Navbar";

import CookieBanner from "./components/CookieBanner/CookieBanner";
import CustomCursor from "./components/CustomCursor/CustomCursor";
import Footer from "./components/Footer/Footer";
import LanguageSwitcher from "./components/LanguageSwitcher/LanguageSwitcher";
import PublicRoute from "./components/PublicRoute/PublicRoute";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import SeoHead from "./components/SeoHead/SeoHead";
import {
  EXPORT_PATHS,
  getHomePath,
  LOCALIZED_ROUTE_PATTERN,
  ROOT_PATH,
  ROUTE_PATHS,
  ROUTE_SLUGS,
} from "./config/routes";
import useNavItems from "./hooks/useNavItems";
import Home from "./pages/Home/Home";
import { loggerService, LogTag } from "./Utils/LoggerService";

// Non-critical routes are code-split so they stay out of the initial bundle.
const Vitrines = lazy(() => import("./pages/Vitrines/Vitrines"));
const WhyWebsite = lazy(() => import("./pages/WhyWebsite/WhyWebsite"));
const Audit = lazy(() => import("./pages/Audit/Audit"));
const NotFound = lazy(() => import("./pages/NotFound/NotFound"));
const LinkedInBanner = lazy(
  () => import("./pages/LinkedInBanner/LinkedInBanner"),
);
const XBanner = lazy(() => import("./pages/XBanner/XBanner"));
const LinkedInPostExport = lazy(() =>
  import("./pages/LinkedInPost/LinkedInPost").then((m) => ({
    default: m.LinkedInPostExport,
  })),
);
const InstaStoryExport = lazy(() =>
  import("./pages/InstaStory/InstaStory").then((m) => ({
    default: m.InstaStoryExport,
  })),
);
const ResumePdfExport = lazy(() =>
  import("./pages/Resume/Resume").then((m) => ({ default: m.ResumePdfExport })),
);
const OnePagerExport = lazy(() => import("./pages/OnePager/OnePager"));
const HarnessExport = lazy(() => import("./pages/Harness/Harness"));
const BlogList = lazy(() => import("./pages/Blog/BlogList/BlogList"));
const BlogArticle = lazy(() => import("./pages/Blog/BlogArticle/BlogArticle"));
const Login = lazy(() => import("./pages/Login/Login"));

const SUPPORTED_LANGS = ["en", "fr"] as const;
type SupportedLang = (typeof SUPPORTED_LANGS)[number];

function detectLang(): SupportedLang {
  const stored = localStorage.getItem("i18nextLng");
  if (stored && SUPPORTED_LANGS.includes(stored as SupportedLang)) {
    return stored as SupportedLang;
  }
  const browser = navigator.language.split("-")[0];
  return SUPPORTED_LANGS.includes(browser as SupportedLang)
    ? (browser as SupportedLang)
    : "en";
}

function LangRedirect() {
  return <Navigate to={getHomePath(detectLang())} replace />;
}

function LocalizedApp() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const navItems = useNavItems();

  const validLang = SUPPORTED_LANGS.includes(lang as SupportedLang)
    ? (lang as SupportedLang)
    : null;

  useEffect(() => {
    if (!validLang) {
      void navigate(ROOT_PATH, { replace: true });
      return;
    }
    if (i18n.resolvedLanguage !== validLang) {
      i18n.changeLanguage(validLang).catch((error: unknown) => {
        loggerService.error(LogTag.APP, "Language switch failed", error);
      });
    }
    localStorage.setItem("i18nextLng", validLang);
    document.documentElement.lang = validLang;
  }, [validLang, i18n, navigate]);

  if (!validLang) return null;

  return (
    <div className="App">
      <a className="skipLink" href="#main-content">
        {t("a11y.skip_to_content")}
      </a>
      <SeoHead />
      <ScrollToTop />
      <CustomCursor />
      <Navbar
        navItems={navItems}
        mobileBeforeChildren={<LanguageSwitcher mobile />}
      >
        <LanguageSwitcher />
      </Navbar>
      <main id="main-content">
        <Suspense fallback={null}>
          <Routes>
            <Route index element={<Home />} />
            <Route path={ROUTE_SLUGS.vitrines.fr} element={<Vitrines />} />
            <Route path={ROUTE_SLUGS.vitrines.en} element={<Vitrines />} />
            <Route path={ROUTE_SLUGS.whyWebsite.fr} element={<WhyWebsite />} />
            <Route path={ROUTE_SLUGS.whyWebsite.en} element={<WhyWebsite />} />
            {/* fr and en share the same audit slug, so a single route covers both. */}
            <Route path={ROUTE_SLUGS.audit.fr} element={<Audit />} />
            {/* Blog (fr and en share the "blog" slug, so one route covers both). */}
            <Route path={ROUTE_PATHS.blog} element={<BlogList />} />
            <Route path={ROUTE_PATHS.blogArticle} element={<BlogArticle />} />
            {/* Public-only: redirects authenticated users away from login. */}
            <Route element={<PublicRoute />}>
              <Route path={ROUTE_PATHS.login} element={<Login />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <CookieBanner />
      <Toaster position="bottom-right" reverseOrder={false} />
    </div>
  );
}

function App() {
  return (
    <Router>
      <Suspense fallback={null}>
        <Routes>
          {import.meta.env.DEV && (
            <>
              <Route
                path={EXPORT_PATHS.linkedInBanner}
                element={<LinkedInBanner />}
              />
              <Route path={EXPORT_PATHS.xBanner} element={<XBanner />} />
              <Route
                path={EXPORT_PATHS.linkedInPost}
                element={<LinkedInPostExport />}
              />
              <Route
                path={EXPORT_PATHS.instaStory}
                element={<InstaStoryExport />}
              />
              <Route path={EXPORT_PATHS.resume} element={<ResumePdfExport />} />
              <Route
                path={EXPORT_PATHS.onePager}
                element={<OnePagerExport />}
              />
              <Route path={EXPORT_PATHS.harness} element={<HarnessExport />} />
            </>
          )}
          <Route path={LOCALIZED_ROUTE_PATTERN} element={<LocalizedApp />} />
          <Route index element={<LangRedirect />} />
          <Route path="*" element={<Navigate to={ROOT_PATH} replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
